import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Table } from '@app/shared/models';
import { TABLE_STATUS_COLORS, TABLE_STATUS_LABELS } from '@app/shared/enums';
import { SimulationService, TableService } from '@app/shared/services';

interface DragState {
  isDragging: boolean;
  table: Table | null;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

@Component({
  selector: 'app-table-grid',
  templateUrl: './table-grid.component.html',
  styleUrls: ['./table-grid.component.scss']
})
export class TableGridComponent implements OnInit, OnDestroy, OnChanges {
  @Input() tables: Table[] = [];
  @Input() selectedTables: Set<string> = new Set();
  @Input() selectedZoneId: string | null = null;
  @Input() canCreate = true;
  @Input() canEdit = true;
  @Output() tableClick = new EventEmitter<[Table, MouseEvent]>();
  @Output() tableDoubleClick = new EventEmitter<Table>();
  @Output() tableMoved = new EventEmitter<{ table: Table; x: number; y: number }>();
  @Output() tablesOrganized = new EventEmitter<{ table: Table; x: number; y: number }[]>();
  @Output() createTable = new EventEmitter<void>();
  @Output() deleteTable = new EventEmitter<Table>();

  @ViewChild('gridContainer', { static: true }) gridContainer!: ElementRef<HTMLDivElement>;

  dragState: DragState = {
    isDragging: false,
    table: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0
  };

  zoomLevel = 1;
  panX = 0;
  panY = 0;
  isPanning = false;
  panStartX = 0;
  panStartY = 0;
  snapToGrid = true;
  gridSize = 20;
  showOrganizeMenu = false;
  tableSpacing = 120;

  /** Rango de zoom: mínimo (ver todo) y máximo (detalle) */
  private static readonly ZOOM_MIN = 0.15;
  private static readonly ZOOM_MAX = 2;
  /** Tamaño en px de cada mesa en el canvas (debe coincidir con CSS .table-item) */
  private static readonly TABLE_ITEM_SIZE = 100;

  private dragTable: Table | null = null;
  private dragOffset = { x: 0, y: 0 };

  // Simulador
  simulationState$ = this.simulationService.state$;
  showSimulationReport = false;

  constructor(
    private simulationService: SimulationService,
    private tableService: TableService
  ) {}

  ngOnInit(): void {
    // Inicializar posiciones si no existen
    this.tables.forEach(table => {
      if (table.positionX === undefined || table.positionY === undefined) {
        table.positionX = Math.random() * 500;
        table.positionY = Math.random() * 400;
      }
    });
  }

  ngOnChanges(): void {
    // Cambios en las props
  }

  ngOnDestroy(): void {
    // Cleanup si es necesario
  }

  getTableColor(table: Table): string {
    return TABLE_STATUS_COLORS[table.status];
  }

  getTableStatusLabel(table: Table): string {
    return TABLE_STATUS_LABELS[table.status];
  }

  getTableStatusIcon(table: Table): string {
    const icons: Record<string, string> = {
      'AVAILABLE': 'fas fa-circle',
      'OCCUPIED': 'fas fa-users',
      'ORDER_IN_PROGRESS': 'fas fa-utensils',
      'SERVING': 'fas fa-concierge-bell',
      'PENDING_PAYMENT': 'fas fa-credit-card'
    };
    return icons[table.status] || 'fas fa-circle';
  }

  isTableSelected(tableId: string): boolean {
    return this.selectedTables.has(tableId);
  }

  onTableClick(table: Table, event: MouseEvent): void {
    // No emitir click si estamos en modo drag
    if (!this.dragState.isDragging) {
      this.tableClick.emit([table, event]);
    }
  }

  onTableDoubleClick(table: Table): void {
    if (!this.dragState.isDragging) {
      this.tableDoubleClick.emit(table);
    }
  }

  // Drag & Drop Handlers
  onTableMouseDown(table: Table, event: MouseEvent): void {
    if (!this.canEdit) return;
    // Solo permitir drag con botón izquierdo
    if (event.button !== 0) return;
    
    // Prevenir drag si se está haciendo multi-selección o pan
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (!this.gridContainer) return;

    const rect = this.gridContainer.nativeElement.getBoundingClientRect();
    const tableElement = (event.currentTarget as HTMLElement);
    const tableRect = tableElement.getBoundingClientRect();

    // Calcular offset considerando el zoom y pan
    const scaledX = table.positionX * this.zoomLevel;
    const scaledY = table.positionY * this.zoomLevel;
    
    this.dragState = {
      isDragging: true,
      table: table,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: (event.clientX - rect.left - this.panX) / this.zoomLevel - table.positionX,
      offsetY: (event.clientY - rect.top - this.panY) / this.zoomLevel - table.positionY
    };

    this.dragTable = table;
    tableElement.style.cursor = 'grabbing';
    tableElement.style.zIndex = '1000';
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    // Manejar drag de mesa
    if (this.dragState.isDragging && this.dragState.table && this.gridContainer) {
      event.preventDefault();

      const rect = this.gridContainer.nativeElement.getBoundingClientRect();
      // Calcular nueva posición considerando zoom y pan
      let newX = (event.clientX - rect.left - this.panX) / this.zoomLevel - this.dragState.offsetX;
      let newY = (event.clientY - rect.top - this.panY) / this.zoomLevel - this.dragState.offsetY;

      // Snap to grid si está habilitado
      if (this.snapToGrid) {
        newX = Math.round(newX / this.gridSize) * this.gridSize;
        newY = Math.round(newY / this.gridSize) * this.gridSize;
      }

      // Limitar dentro del contenedor (considerando zoom)
      const maxX = (rect.width / this.zoomLevel) - 100;
      const maxY = (rect.height / this.zoomLevel) - 100;
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));

      // Actualizar posición visualmente
      this.dragState.table.positionX = newX;
      this.dragState.table.positionY = newY;
      return;
    }


    // Manejar pan del grid
    if (this.isPanning) {
      event.preventDefault();
      this.panX = event.clientX - this.panStartX;
      this.panY = event.clientY - this.panStartY;
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    if (this.dragState.isDragging && this.dragState.table) {
      // Emitir evento de movimiento
      this.tableMoved.emit({
        table: this.dragState.table,
        x: this.dragState.table.positionX,
        y: this.dragState.table.positionY
      });

      // Resetear estado
      this.dragState.isDragging = false;
      this.dragState.table = null;
      this.dragTable = null;

      // Resetear cursor
      const elements = document.querySelectorAll('.table-item');
      elements.forEach(el => {
        (el as HTMLElement).style.cursor = '';
        (el as HTMLElement).style.zIndex = '';
      });
    }


    if (this.isPanning) {
      this.isPanning = false;
      if (this.gridContainer) {
        this.gridContainer.nativeElement.style.cursor = '';
      }
    }

    // Cerrar menú de organización si se hace clic fuera
    if (this.showOrganizeMenu) {
      const target = event.target as HTMLElement;
      if (!target.closest('.organize-menu-wrapper')) {
        this.closeOrganizeMenu();
      }
    }
  }

  // Pan handlers
  onGridMouseDown(event: MouseEvent): void {
    // Solo permitir pan si no se está arrastrando una mesa y se presiona Ctrl
    if (event.button === 0 && (event.ctrlKey || event.metaKey) && !this.dragState.isDragging) {
      event.preventDefault();
      this.isPanning = true;
      this.panStartX = event.clientX - this.panX;
      this.panStartY = event.clientY - this.panY;
      if (this.gridContainer) {
        this.gridContainer.nativeElement.style.cursor = 'grabbing';
      }
    }
  }

  // Zoom handlers (rueda hace zoom con o sin Ctrl para mayor usabilidad)
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.92 : 1.08;
    const oldZoom = this.zoomLevel;
    this.zoomLevel = Math.max(
      TableGridComponent.ZOOM_MIN,
      Math.min(TableGridComponent.ZOOM_MAX, this.zoomLevel * delta)
    );
    if (this.gridContainer) {
      const rect = this.gridContainer.nativeElement.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      const zoomChange = this.zoomLevel / oldZoom;
      this.panX = mouseX - (mouseX - this.panX) * zoomChange;
      this.panY = mouseY - (mouseY - this.panY) * zoomChange;
    }
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(TableGridComponent.ZOOM_MAX, this.zoomLevel + 0.15);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(TableGridComponent.ZOOM_MIN, this.zoomLevel - 0.15);
  }

  resetZoom(): void {
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;
  }

  /**
   * Ajusta zoom y pan para que todas las mesas quepan en pantalla (con margen).
   * Resuelve "ver todas las mesas" y zoom que no permitía alejar suficiente.
   */
  fitAllTablesInView(): void {
    if (!this.gridContainer || this.tables.length === 0) {
      this.resetZoom();
      return;
    }
    const rect = this.gridContainer.nativeElement.getBoundingClientRect();
    const padding = 48;
    const w = rect.width - padding * 2;
    const h = rect.height - padding * 2;
    if (w <= 0 || h <= 0) return;

    const size = TableGridComponent.TABLE_ITEM_SIZE;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const t of this.tables) {
      const x = t.positionX ?? 0;
      const y = t.positionY ?? 0;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + size);
      maxY = Math.max(maxY, y + size);
    }
    const boxW = Math.max(maxX - minX, size);
    const boxH = Math.max(maxY - minY, size);
    const scale = Math.min(w / boxW, h / boxH, TableGridComponent.ZOOM_MAX);
    const zoom = Math.max(TableGridComponent.ZOOM_MIN, Math.min(TableGridComponent.ZOOM_MAX, scale));
    const centerX = minX + boxW / 2;
    const centerY = minY + boxH / 2;
    this.zoomLevel = zoom;
    this.panX = rect.width / 2 - centerX * zoom;
    this.panY = rect.height / 2 - centerY * zoom;
  }

  toggleSnapToGrid(): void {
    this.snapToGrid = !this.snapToGrid;
  }

  getTableStyle(table: Table): Record<string, string | number> {
    const isDragging = this.dragState.isDragging && this.dragState.table?.id === table.id;
    
    return {
      left: `${table.positionX}px`,
      top: `${table.positionY}px`,
      backgroundColor: this.getTableColor(table),
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 1000 : (this.isTableSelected(table.id) ? 100 : 1)
    };
  }

  getGridStyle(): Record<string, string> {
    return {
      transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`,
      transformOrigin: 'top left'
    };
  }

  isTableDragging(tableId: string): boolean {
    return this.dragState.isDragging && this.dragState.table?.id === tableId;
  }

  getStatusList(): string[] {
    return ['AVAILABLE', 'OCCUPIED', 'ORDER_IN_PROGRESS', 'SERVING', 'PENDING_PAYMENT'];
  }

  getStatusColor(status: string): string {
    return TABLE_STATUS_COLORS[status as keyof typeof TABLE_STATUS_COLORS] || '#ccc';
  }

  getStatusLabel(status: string): string {
    return TABLE_STATUS_LABELS[status as keyof typeof TABLE_STATUS_LABELS] || status;
  }

  // Exponer Math para usar en template
  Math = Math;

  // Funciones de organización automática
  toggleOrganizeMenu(): void {
    this.showOrganizeMenu = !this.showOrganizeMenu;
  }

  closeOrganizeMenu(): void {
    this.showOrganizeMenu = false;
  }

  organizeTablesGrid(): void {
    if (!this.gridContainer || this.tables.length === 0) return;

    const tables = [...this.tables].sort((a, b) => a.number - b.number);
    const cols = Math.ceil(Math.sqrt(tables.length));
    const startX = 50;
    const startY = 50;
    const spacing = this.tableSpacing;

    const organized: { table: Table; x: number; y: number }[] = [];

    tables.forEach((table, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      const x = startX + (col * spacing);
      const y = startY + (row * spacing);

      table.positionX = x;
      table.positionY = y;
      organized.push({ table, x, y });
    });

    this.tablesOrganized.emit(organized);
    this.closeOrganizeMenu();
    this.resetZoom();
  }

  organizeTablesLShape(): void {
    if (!this.gridContainer || this.tables.length === 0) return;

    const tables = [...this.tables].sort((a, b) => a.number - b.number);
    const total = tables.length;
    const verticalCount = Math.ceil(total / 2);
    const horizontalCount = total - verticalCount;
    const startX = 50;
    const startY = 50;
    const spacing = this.tableSpacing;

    const organized: { table: Table; x: number; y: number }[] = [];

    // Parte vertical de la L
    for (let i = 0; i < verticalCount; i++) {
      const table = tables[i];
      table.positionX = startX;
      table.positionY = startY + (i * spacing);
      organized.push({ table, x: table.positionX, y: table.positionY });
    }

    // Parte horizontal de la L
    for (let i = 0; i < horizontalCount; i++) {
      const table = tables[verticalCount + i];
      table.positionX = startX + ((i + 1) * spacing);
      table.positionY = startY + ((verticalCount - 1) * spacing);
      organized.push({ table, x: table.positionX, y: table.positionY });
    }

    this.tablesOrganized.emit(organized);
    this.closeOrganizeMenu();
    this.resetZoom();
  }

  organizeTablesCircular(): void {
    if (!this.gridContainer || this.tables.length === 0) return;

    const tables = [...this.tables].sort((a, b) => a.number - b.number);
    const centerX = 400;
    const centerY = 300;
    const radius = Math.max(150, tables.length * 15);
    const angleStep = (2 * Math.PI) / tables.length;

    const organized: { table: Table; x: number; y: number }[] = [];

    tables.forEach((table, index) => {
      const angle = index * angleStep - (Math.PI / 2); // Empezar desde arriba
      const x = centerX + (radius * Math.cos(angle)) - 50; // -50 para centrar la mesa
      const y = centerY + (radius * Math.sin(angle)) - 50;

      table.positionX = x;
      table.positionY = y;
      organized.push({ table, x, y });
    });

    this.tablesOrganized.emit(organized);
    this.closeOrganizeMenu();
    this.resetZoom();
  }

  organizeTablesLinear(): void {
    if (!this.gridContainer || this.tables.length === 0) return;

    const tables = [...this.tables].sort((a, b) => a.number - b.number);
    const startX = 50;
    const startY = 200;
    const spacing = this.tableSpacing;

    const organized: { table: Table; x: number; y: number }[] = [];

    tables.forEach((table, index) => {
      const x = startX + (index * spacing);
      const y = startY;

      table.positionX = x;
      table.positionY = y;
      organized.push({ table, x, y });
    });

    this.tablesOrganized.emit(organized);
    this.closeOrganizeMenu();
    this.resetZoom();
  }

  organizeTablesUShape(): void {
    if (!this.gridContainer || this.tables.length === 0) return;

    const tables = [...this.tables].sort((a, b) => a.number - b.number);
    const total = tables.length;
    const sideCount = Math.ceil(total / 3);
    const bottomCount = total - (sideCount * 2);
    const startX = 50;
    const startY = 50;
    const spacing = this.tableSpacing;

    const organized: { table: Table; x: number; y: number }[] = [];
    let index = 0;

    // Lado izquierdo
    for (let i = 0; i < sideCount && index < total; i++) {
      const table = tables[index++];
      table.positionX = startX;
      table.positionY = startY + (i * spacing);
      organized.push({ table, x: table.positionX, y: table.positionY });
    }

    // Parte inferior
    for (let i = 0; i < bottomCount && index < total; i++) {
      const table = tables[index++];
      table.positionX = startX + ((i + 1) * spacing);
      table.positionY = startY + ((sideCount - 1) * spacing);
      organized.push({ table, x: table.positionX, y: table.positionY });
    }

    // Lado derecho
    for (let i = 0; index < total; i++) {
      const table = tables[index++];
      table.positionX = startX + ((bottomCount + 1) * spacing);
      table.positionY = startY + ((sideCount - 2 - i) * spacing);
      organized.push({ table, x: table.positionX, y: table.positionY });
    }

    this.tablesOrganized.emit(organized);
    this.closeOrganizeMenu();
    this.resetZoom();
  }

  organizeTablesTwoRows(): void {
    if (!this.gridContainer || this.tables.length === 0) return;

    const tables = [...this.tables].sort((a, b) => a.number - b.number);
    const midPoint = Math.ceil(tables.length / 2);
    const startX = 50;
    const startY = 150;
    const spacing = this.tableSpacing;

    const organized: { table: Table; x: number; y: number }[] = [];

    // Primera fila
    for (let i = 0; i < midPoint; i++) {
      const table = tables[i];
      table.positionX = startX + (i * spacing);
      table.positionY = startY;
      organized.push({ table, x: table.positionX, y: table.positionY });
    }

    // Segunda fila
    for (let i = 0; i < tables.length - midPoint; i++) {
      const table = tables[midPoint + i];
      table.positionX = startX + (i * spacing);
      table.positionY = startY + spacing;
      organized.push({ table, x: table.positionX, y: table.positionY });
    }

    this.tablesOrganized.emit(organized);
    this.closeOrganizeMenu();
    this.resetZoom();
  }

  // Funciones para crear/eliminar mesas
  onCreateTableClick(): void {
    this.createTable.emit();
  }

  onDeleteTableClick(table: Table, event: MouseEvent): void {
    event.stopPropagation();
    if (confirm(`¿Estás seguro de que quieres eliminar la mesa ${table.number}?`)) {
      this.deleteTable.emit(table);
    }
  }


  // Funciones del Simulador
  startSimulation(): void {
    this.tableService.zones$.subscribe(zones => {
      this.simulationService.startSimulation(zones, 120000); // 2 minutos
    }).unsubscribe();
  }

  pauseSimulation(): void {
    this.simulationService.pauseSimulation();
  }

  resumeSimulation(): void {
    this.simulationService.resumeSimulation();
  }

  stopSimulation(): void {
    this.simulationService.stopSimulation();
  }

  showReport(): void {
    this.showSimulationReport = true;
  }

  closeReport(): void {
    this.showSimulationReport = false;
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  formatDuration(milliseconds: number): string {
    if (milliseconds === 0) return '0s';
    const seconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  }

  calculateCapacity(state: any): number {
    // Calcular capacidad basada en tiempo promedio de servicio y número de mesas
    const avgServiceTime = state.metrics.averageServiceTime || 30000; // 30 segundos por defecto
    if (avgServiceTime === 0) return 0;
    
    const totalTime = state.elapsedTime * 1000; // convertir a ms
    const tables = this.tables.length;
    if (tables === 0) return 0;
    
    // Calcular cuántos ciclos completos se pueden hacer en el tiempo total
    const cycles = Math.floor(totalTime / avgServiceTime);
    // Considerar eficiencia del 80% y capacidad promedio de las mesas
    const avgCapacity = this.tables.reduce((sum, t) => sum + t.capacity, 0) / tables;
    return Math.floor(cycles * tables * avgCapacity * 0.8);
  }
}
